/**
 * Thin wrapper around window.Telegram.WebApp.
 *
 * Kept deliberately small and defensive: every call checks the SDK is
 * present so the app still runs (with sane fallbacks) when opened as a
 * plain browser tab during development, per the "avoid heavy JS, keep it
 * boringly reliable" spirit of section 3.2 / 4.2 of the TЗ.
 */

type ThemeParams = Record<string, string>;

interface TelegramWebApp {
  ready: () => void;
  expand: () => void;
  requestFullscreen?: () => void;
  close: () => void;
  colorScheme: "light" | "dark";
  themeParams: ThemeParams;
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name: string;
      last_name?: string;
      username?: string;
      language_code?: string;
    };
    /** ?startapp= parameter of the Mini App link. */
    start_param?: string;
  };
  onEvent: (event: string, cb: () => void) => void;
  offEvent: (event: string, cb: () => void) => void;
  HapticFeedback?: {
    impactOccurred: (style: "light" | "medium" | "heavy" | "rigid" | "soft") => void;
    notificationOccurred: (type: "error" | "success" | "warning") => void;
    selectionChanged: () => void;
  };
  MainButton: {
    text: string;
    color?: string;
    textColor?: string;
    isVisible: boolean;
    isActive: boolean;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    setText: (text: string) => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
    showProgress: (leaveActive?: boolean) => void;
    hideProgress: () => void;
  };
  BackButton: {
    isVisible: boolean;
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  CloudStorage?: {
    getItem: (key: string, cb: (err: unknown, value?: string) => void) => void;
    setItem: (key: string, value: string, cb?: (err: unknown, ok?: boolean) => void) => void;
    removeItem: (key: string, cb?: (err: unknown, ok?: boolean) => void) => void;
  };
  showConfirm: (message: string, cb: (ok: boolean) => void) => void;
  showAlert: (message: string, cb?: () => void) => void;
  openLink?: (url: string, options?: { try_instant_view?: boolean }) => void;
}

export function getWebApp(): TelegramWebApp | null {
  const w = window as unknown as { Telegram?: { WebApp?: TelegramWebApp } };
  return w.Telegram?.WebApp ?? null;
}

/**
 * The telegram-web-app.js script defines window.Telegram.WebApp even in a
 * plain browser tab, so "the object exists" is NOT a valid check. A real
 * Mini App session always has a non-empty signed initData string.
 */
export const isInsideTelegram = (): boolean => {
  const tg = getWebApp();
  return !!tg && typeof tg.initData === "string" && tg.initData.length > 0;
};

/**
 * Call once on app boot. Sets full-screen portrait mode, hooks theme +
 * safe-area changes. See 4.2 "Обработка событий: viewportChanged,
 * safeAreaChanged, themeChanged".
 */
export function initTelegram(onThemeChange: (scheme: "light" | "dark") => void): void {
  const tg = getWebApp();
  if (!tg || !isInsideTelegram()) {
    // Dev-in-browser fallback: respect the OS theme so the app is still usable.
    const prefersDark = window.matchMedia?.("(prefers-color-scheme: dark)").matches;
    onThemeChange(prefersDark ? "dark" : "light");
    return;
  }

  tg.ready();
  // Full-size (not fullscreen): Telegram keeps its own header with "Закрыть",
  // and nothing of ours sits under the status bar.
  tg.expand();

  // If fullscreen is ever switched on (e.g. in @BotFather), keep content clear of
  // the status bar and Telegram's floating buttons using the insets Telegram reports.
  applySafeArea();
  for (const ev of ["safeAreaChanged", "contentSafeAreaChanged", "fullscreenChanged", "viewportChanged"]) {
    try {
      tg.onEvent(ev as never, applySafeArea);
    } catch {
      /* older Telegram versions don't know these events */
    }
  }

  onThemeChange(tg.colorScheme);
  tg.onEvent("themeChanged", () => onThemeChange(tg.colorScheme));
}

/**
 * Publishes --kms-inset-top / --kms-inset-bottom for the CSS. In fullscreen
 * they are the system inset plus Telegram's own buttons; otherwise 0 (the web
 * view already starts below Telegram's header), so CSS falls back to env().
 */
function applySafeArea(): void {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const w = getWebApp() as any;
  const root = document.documentElement;
  if (!w || !w.isFullscreen) {
    root.style.removeProperty("--kms-inset-top");
    root.style.removeProperty("--kms-inset-bottom");
    return;
  }
  const sys = w.safeAreaInset ?? {};
  const content = w.contentSafeAreaInset ?? {};
  root.style.setProperty("--kms-inset-top", `${(sys.top ?? 0) + (content.top ?? 0)}px`);
  root.style.setProperty("--kms-inset-bottom", `${(sys.bottom ?? 0) + (content.bottom ?? 0)}px`);
}

export function getTelegramUser() {
  return getWebApp()?.initDataUnsafe.user ?? null;
}

/**
 * Raw initData string, to be sent as-is to the backend on every request.
 * The backend re-validates the HMAC-SHA256 signature against the bot token
 * and checks `auth_date` recency before trusting anything in it (see
 * discovery notes on initData validation) — the client never trusts this
 * data for anything beyond pre-filling a display name.
 */
export function getInitData(): string {
  return getWebApp()?.initData ?? "";
}

export function haptic(kind: "light" | "medium" | "heavy" | "success" | "warning" | "error" | "selection") {
  const tg = getWebApp();
  if (!tg?.HapticFeedback) return;
  if (kind === "success" || kind === "warning" || kind === "error") {
    tg.HapticFeedback.notificationOccurred(kind);
  } else if (kind === "selection") {
    tg.HapticFeedback.selectionChanged();
  } else {
    tg.HapticFeedback.impactOccurred(kind);
  }
}

/** Wires Telegram's native MainButton to a label + handler, and tears it down on unmount. */
export function useMainButtonBinding() {
  // Implemented as a plain function (not a hook) that returns bind/unbind,
  // consumed from a small useEffect in each page — see components' usage.
  return {
    bind(text: string, onClick: () => void, opts?: { disabled?: boolean; loading?: boolean }) {
      const tg = getWebApp();
      if (!tg) return () => {};
      tg.MainButton.setText(text);
      opts?.disabled ? tg.MainButton.disable() : tg.MainButton.enable();
      opts?.loading ? tg.MainButton.showProgress(true) : tg.MainButton.hideProgress();
      tg.MainButton.show();
      tg.MainButton.onClick(onClick);
      return () => {
        tg.MainButton.offClick(onClick);
        tg.MainButton.hide();
      };
    },
  };
}

export function bindBackButton(onClick: () => void): () => void {
  const tg = getWebApp();
  if (!tg) return () => {};
  tg.BackButton.show();
  tg.BackButton.onClick(onClick);
  return () => {
    tg.BackButton.offClick(onClick);
    tg.BackButton.hide();
  };
}

export function confirmAction(message: string): Promise<boolean> {
  const tg = getWebApp();
  if (!tg) return Promise.resolve(window.confirm(message));
  return new Promise((resolve) => tg.showConfirm(message, resolve));
}

export function alertUser(message: string): Promise<void> {
  const tg = getWebApp();
  if (!tg) {
    window.alert(message);
    return Promise.resolve();
  }
  return new Promise((resolve) => tg.showAlert(message, () => resolve()));
}

export function cloudStorage() {
  const tg = getWebApp();
  return tg?.CloudStorage ?? null;
}

/** Opens an external URL (e.g. a Payme/Click checkout page) without closing the Mini App; falls back to a new tab outside Telegram. */
export function openExternalLink(url: string): void {
  const tg = getWebApp();
  if (tg?.openLink) {
    tg.openLink(url);
  } else {
    window.open(url, "_blank");
  }
}

/** Payload of a t.me/<bot>?startapp=… link the app was opened with (e.g. "wl_ab12cd34"). */
export function getStartParam(): string | null {
  return getWebApp()?.initDataUnsafe.start_param ?? null;
}

// ---------- Newer Telegram Mini App features (each checks support first) ----------

/* eslint-disable @typescript-eslint/no-explicit-any */
const wa = (): any => getWebApp();
const supports = (version: string): boolean => {
  const w = wa();
  return !!w && typeof w.isVersionAtLeast === "function" && w.isVersionAtLeast(version);
};

/** "Add to home screen" (Bot API 8.0). Resolves "unsupported" | "added" | "missed" | "unknown". */
export function homeScreenStatus(): Promise<string> {
  return new Promise((resolve) => {
    const w = wa();
    if (!supports("8.0") || typeof w.checkHomeScreenStatus !== "function") return resolve("unsupported");
    try {
      w.checkHomeScreenStatus((status: string) => resolve(status || "unknown"));
    } catch {
      resolve("unsupported");
    }
  });
}
export function addToHomeScreen(): void {
  const w = wa();
  if (supports("8.0") && typeof w.addToHomeScreen === "function") w.addToHomeScreen();
}

/** Opens the chat picker with "@bot <query>" typed in — the product card appears there. */
export function canShareToChat(): boolean {
  return supports("6.7") && typeof wa()?.switchInlineQuery === "function";
}
export function shareToChat(query: string): void {
  wa()?.switchInlineQuery(query, ["users", "groups", "channels"]);
}

/** Telegram Stories (Bot API 7.8). */
export function canShareToStory(): boolean {
  return supports("7.8") && typeof wa()?.shareToStory === "function";
}
export function shareToStory(mediaUrl: string, text: string, link?: string | null): void {
  wa()?.shareToStory(mediaUrl, { text: text.slice(0, 200), ...(link ? { widget_link: { url: link, name: "KMS ROASTING" } } : {}) });
}

/** "Поделиться номером" — resolves the phone number (digits with +) or null if declined/unsupported. */
export function requestPhone(): Promise<string | null> {
  return new Promise((resolve) => {
    const w = wa();
    if (!supports("6.9") || typeof w.requestContact !== "function") return resolve(null);
    try {
      w.requestContact((ok: boolean, res: any) => {
        const phone = ok ? res?.responseUnsafe?.contact?.phone_number : null;
        resolve(phone ? (String(phone).startsWith("+") ? String(phone) : `+${phone}`) : null);
      });
    } catch {
      resolve(null);
    }
  });
}
export function canRequestPhone(): boolean {
  return supports("6.9") && typeof wa()?.requestContact === "function";
}

/** Telegram CloudStorage: the same data on phone and desktop (1024 keys, 4096 chars each). */
export function cloudGet(key: string): Promise<string | null> {
  return new Promise((resolve) => {
    const cs = wa()?.CloudStorage;
    if (!supports("6.9") || !cs) return resolve(null);
    try {
      cs.getItem(key, (err: unknown, value: string) => resolve(err ? null : value || null));
    } catch {
      resolve(null);
    }
  });
}
export function cloudSet(key: string, value: string): void {
  const cs = wa()?.CloudStorage;
  if (!supports("6.9") || !cs || value.length > 4000) return;
  try {
    cs.setItem(key, value, () => {});
  } catch {
    /* ignore */
  }
}
